import { useEffect, useState } from 'react';
import { useReadLocalStorage } from 'usehooks-ts';
import { buildInventoryImageUrl, productPlaceholderImage } from '@utils';
import { AvailableSKU } from '@forms/client/CreateInboundShipmentNoteForm/types';
import { AvailableProduct } from '@hooks';
import { PreSelectedSku } from './types';

export interface ItemDetails {
  kind: 'product' | 'sku';
  name: string;
  subtitle?: string | null;
  imageUrl: string | null;
  unitOfMeasure?: string;
  amountOfProductInSku?: number;
  dimensions?: {
    length: number | null;
    width: number | null;
    height: number | null;
    unit: string;
  } | null;
  netVolume?: number | null;
  netVolumeUnit?: string;
  availableStock?: number | null;
  availableProducts?: number | null;
}

function availableStockIn(
  stockByWarehouse:
    | { warehouseId: string; availableStock: number }[]
    | undefined,
  warehouseId?: string
): number | null {
  if (!warehouseId || !stockByWarehouse?.length) return null;
  const entry = stockByWarehouse.find(
    (stock) => stock.warehouseId === warehouseId
  );
  return entry ? entry.availableStock ?? null : null;
}

function subtitleFrom(
  brand?: string | null,
  category?: string | null
): string | null {
  return [brand, category].filter(Boolean).join(' · ') || null;
}

export function skuToDetails(
  sku: AvailableSKU | PreSelectedSku,
  warehouseId?: string
): ItemDetails {
  return {
    kind: 'sku',
    name: sku.name,
    subtitle: sku.skuId || null,
    imageUrl: buildInventoryImageUrl(sku.images?.[0]),
    unitOfMeasure: sku.unitOfMeasure,
    amountOfProductInSku: sku.productDetails?.amountOfProductInSku,
    dimensions: sku.dimensions
      ? {
          length: sku.dimensions.length,
          width: sku.dimensions.width,
          height: sku.dimensions.height,
          unit: sku.dimensions.dimensionsUnitOfMeasure,
        }
      : null,
    netVolume: sku.productDetails?.netVolume ?? null,
    netVolumeUnit: sku.productDetails?.netVolumeUnitOfMeasure,
    availableStock: availableStockIn(sku.stockByWarehouse, warehouseId),
  };
}

export function preSelectedToProductDetails(
  pre: PreSelectedSku,
  warehouseId?: string
): ItemDetails {
  const { productDetails } = pre;
  const factor =
    productDetails?.amountOfProductInSku ?? pre.amountOfProductInSku ?? 1;
  const availableUnits = availableStockIn(pre.stockByWarehouse, warehouseId);
  return {
    kind: 'product',
    name: productDetails?.name ?? pre.name,
    subtitle: subtitleFrom(productDetails?.brand, productDetails?.category),
    imageUrl:
      buildInventoryImageUrl(productDetails?.image) ??
      buildInventoryImageUrl(pre.images?.[0]),
    unitOfMeasure: pre.unitOfMeasure,
    amountOfProductInSku: factor,
    dimensions: pre.dimensions
      ? {
          length: pre.dimensions.length,
          width: pre.dimensions.width,
          height: pre.dimensions.height,
          unit: pre.dimensions.dimensionsUnitOfMeasure,
        }
      : null,
    netVolume: productDetails?.netVolume ?? null,
    netVolumeUnit: productDetails?.netVolumeUnitOfMeasure,
    availableProducts: availableUnits != null ? availableUnits * factor : null,
  };
}

export function productToDetails(
  product: AvailableProduct,
  warehouseId?: string
): ItemDetails {
  const representative = product.skus[0];
  const productDetails = representative?.productDetails;
  const factor = productDetails?.amountOfProductInSku ?? 1;
  const availableProducts = warehouseId
    ? product.skus.reduce((total, sku) => {
        const perSku = sku.productDetails?.amountOfProductInSku ?? 1;
        const units = availableStockIn(sku.stockByWarehouse, warehouseId) ?? 0;
        return total + units * perSku;
      }, 0)
    : null;
  return {
    kind: 'product',
    name: product.name,
    subtitle: subtitleFrom(productDetails?.brand, productDetails?.category),
    imageUrl: buildInventoryImageUrl(representative?.images?.[0]),
    unitOfMeasure: representative?.unitOfMeasure,
    amountOfProductInSku: factor,
    dimensions: representative?.dimensions
      ? {
          length: representative.dimensions.length,
          width: representative.dimensions.width,
          height: representative.dimensions.height,
          unit: representative.dimensions.dimensionsUnitOfMeasure,
        }
      : null,
    netVolume: productDetails?.netVolume ?? null,
    netVolumeUnit: productDetails?.netVolumeUnitOfMeasure,
    availableProducts,
  };
}

function unitNounFor(unitOfMeasure?: string): string {
  return unitOfMeasure && unitOfMeasure.toLowerCase() !== 'other'
    ? unitOfMeasure
    : 'SKU';
}

function formatNumber(value: number): string {
  return value.toLocaleString();
}

function formatDimensions(
  dimensions: ItemDetails['dimensions']
): string | null {
  if (!dimensions) return null;
  const { length, width, height, unit } = dimensions;
  if (length == null || width == null || height == null) return null;
  return `${length} × ${width} × ${height} ${unit}`;
}

export function ItemDetailsPanel({
  details,
  className,
}: {
  details: ItemDetails;
  className?: string;
}) {
  const darkMode = useReadLocalStorage('usehooks-ts-dark-mode');
  const [imageErrored, setImageErrored] = useState(false);

  useEffect(() => {
    setImageErrored(false);
  }, [details.imageUrl]);

  const placeholder = productPlaceholderImage(!!darkMode);
  const showPlaceholder = imageErrored || !details.imageUrl;
  const imageSrc = showPlaceholder
    ? placeholder
    : details.imageUrl ?? placeholder;
  const unitNoun = unitNounFor(details.unitOfMeasure);
  const showPacked = (details.amountOfProductInSku ?? 0) > 1;
  const dimensionsLabel = formatDimensions(details.dimensions);

  const facts: { label: string; value: string }[] = [];
  if (details.kind === 'sku' && details.unitOfMeasure) {
    facts.push({ label: 'Unit', value: details.unitOfMeasure });
  }
  if (showPacked && details.amountOfProductInSku) {
    facts.push({
      label: 'Pack',
      value: `1 ${unitNoun} = ${formatNumber(
        details.amountOfProductInSku
      )} products`,
    });
  }
  if (dimensionsLabel) {
    facts.push({ label: 'Size', value: dimensionsLabel });
  }
  if (details.netVolume != null && details.netVolume > 0) {
    facts.push({
      label: 'Volume',
      value: `${formatNumber(details.netVolume)}${details.netVolumeUnit ?? ''}`,
    });
  }
  if (details.kind === 'sku' && details.availableStock != null) {
    facts.push({
      label: 'Available',
      value: `${formatNumber(
        details.availableStock
      )} ${unitNoun.toLowerCase()}s`,
    });
  }
  if (details.kind === 'product' && details.availableProducts != null) {
    facts.push({
      label: 'Available',
      value: `≈ ${formatNumber(details.availableProducts)} products`,
    });
  }

  return (
    <div
      className={`flex gap-3 rounded-lg border border-gray-200 bg-white p-3 ${
        className ?? ''
      }`}
    >
      <div
        className={`flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border ${
          darkMode
            ? 'border-gray-700 bg-gray-900'
            : 'border-gray-100 bg-gray-100'
        }`}
      >
        <img
          src={imageSrc}
          alt={details.name}
          onError={() => {
            if (!showPlaceholder) setImageErrored(true);
          }}
          className={
            showPlaceholder
              ? 'max-h-full max-w-full object-contain p-2'
              : 'h-full w-full object-cover object-top'
          }
        />
      </div>
      <div className='min-w-0 flex-1'>
        <p className='truncate text-sm font-semibold text-gray-900'>
          {details.name}
        </p>
        {details.subtitle && (
          <p className='mt-0.5 truncate text-xs text-gray-500'>
            {details.subtitle}
          </p>
        )}
        {facts.length > 0 && (
          <div className='mt-2 flex flex-wrap gap-1.5'>
            {facts.map((fact) => (
              <span
                key={fact.label}
                className='inline-flex items-center gap-1 rounded-md bg-gray-50 px-2 py-1 text-xs'
              >
                <span className='text-gray-400'>{fact.label}</span>
                <span className='font-medium text-gray-700'>{fact.value}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
